import React, { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Chip, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField } from '@mui/material';
import { IconPlus, IconHistory } from '@tabler/icons';
import { useHistory, useLocation } from 'react-router-dom';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';

const TIPO_LABEL = {
    entrada: { label: 'Entrada', color: 'success' },
    saida: { label: 'Saída', color: 'error' },
    ajuste: { label: 'Ajuste', color: 'warning' }
};

const MOTIVO_LABEL = {
    venda: 'Venda',
    cancelamento_venda: 'Cancelamento de Venda',
    compra: 'Compra',
    cancelamento_compra: 'Cancelamento de Compra',
    devolucao: 'Devolução',
    perda: 'Perda/Avaria',
    ajuste_manual: 'Ajuste Manual',
    transferencia: 'Transferência entre Depósitos'
};

const useQuery = () => new URLSearchParams(useLocation().search);

const StockMovements = () => {
    const history = useHistory();
    const query = useQuery();
    const produtoVariacaoFilter = query.get('produto_variacao') || '';

    const [appId, setAppId] = useState(null);
    const [movements, setMovements] = useState([]);
    const [tipoFilter, setTipoFilter] = useState('');
    const [warehouses, setWarehouses] = useState([]);
    const [warehouseFilter, setWarehouseFilter] = useState(query.get('warehouse') || '');

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        (async () => {
            const res = await api.get(`v1/apps/${appId}/inventory/warehouses/`);
            setWarehouses(res.data || []);
        })();
    }, [appId]);

    useEffect(() => {
        if (!appId) return;
        listarMovimentos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, tipoFilter, warehouseFilter, produtoVariacaoFilter]);

    const listarMovimentos = async () => {
        const params = new URLSearchParams();
        if (tipoFilter) params.set('tipo', tipoFilter);
        if (warehouseFilter) params.set('warehouse', warehouseFilter);
        if (produtoVariacaoFilter) params.set('produto_variacao', produtoVariacaoFilter);
        const res = await api.get(`v1/apps/${appId}/inventory/movements/?${params.toString()}`);
        setMovements(res.data.movements || []);
    };

    return (
        <Box>
            <PageHeader
                icon={IconHistory}
                gradient="graphite"
                title="Histórico de Movimentações"
                subtitle={
                    produtoVariacaoFilter && (
                        <>
                            Filtrando por uma variação específica -{' '}
                            <Button size="small" onClick={() => history.push('/dashboard/inventory/movements')}>
                                limpar filtro
                            </Button>
                        </>
                    )
                }
                actions={
                    <Stack direction="row" spacing={2}>
                        <TextField
                            select
                            size="small"
                            label="Depósito"
                            value={warehouseFilter}
                            onChange={(e) => setWarehouseFilter(e.target.value)}
                            sx={{ width: 180 }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {warehouses.map((w) => (
                                <MenuItem key={w.id} value={w.id}>
                                    {w.name}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select
                            size="small"
                            label="Tipo"
                            value={tipoFilter}
                            onChange={(e) => setTipoFilter(e.target.value)}
                            sx={{ width: 160 }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            <MenuItem value="entrada">Entrada</MenuItem>
                            <MenuItem value="saida">Saída</MenuItem>
                            <MenuItem value="ajuste">Ajuste</MenuItem>
                        </TextField>

                        <Button
                            variant="contained"
                            startIcon={<IconPlus size={18} />}
                            onClick={() => history.push('/dashboard/inventory/new')}
                        >
                            Novo Movimento
                        </Button>
                    </Stack>
                }
            />

            <Card>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Produto</TableCell>
                                <TableCell>Depósito</TableCell>
                                <TableCell>Tipo</TableCell>
                                <TableCell>Motivo</TableCell>
                                <TableCell align="right">Quantidade</TableCell>
                                <TableCell align="right">Antes → Depois</TableCell>
                                <TableCell>Data</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {movements.map((m) => {
                                const tipoInfo = TIPO_LABEL[m.tipo] || { label: m.tipo, color: 'default' };
                                return (
                                    <TableRow key={m.id}>
                                        <TableCell>{m.produto_variacao_nome}</TableCell>
                                        <TableCell>
                                            {m.warehouse_name}
                                            {m.location_code ? ` (${m.location_code})` : ''}
                                            {m.lot_numero && (
                                                <Chip label={`Lote ${m.lot_numero}`} size="small" variant="outlined" sx={{ ml: 1 }} />
                                            )}
                                            {m.serial_numero && (
                                                <Chip label={`S/N ${m.serial_numero}`} size="small" variant="outlined" sx={{ ml: 1 }} />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={tipoInfo.label} color={tipoInfo.color} size="small" />
                                        </TableCell>
                                        <TableCell>{MOTIVO_LABEL[m.motivo] || m.motivo}</TableCell>
                                        <TableCell align="right">{m.quantidade}</TableCell>
                                        <TableCell align="right">
                                            {m.quantidade_anterior} → {m.quantidade_posterior}
                                        </TableCell>
                                        <TableCell>{new Date(m.created_at).toLocaleString('pt-BR')}</TableCell>
                                    </TableRow>
                                );
                            })}
                            {movements.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        Nenhuma movimentação encontrada
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </Box>
    );
};

export default StockMovements;
